import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import BookingCta from "../components/BookingCta";
import { track, useSiteConfig } from "../lib/site";

const STUDIES = [
  "Omega Institute for Holistic Studies",
  "Health Options Institute",
  "Upledger Institute International",
  "Paul St. John, LMT, CNST",
  "Susan Bradford, RM",
  "Dr. Brian Weiss, M.D.",
  "Bob Wolfe, BA",
];

export default function About() {
  const config = useSiteConfig();
  const b = config.business;

  return (
    <>
      <Seo
        title="About Dorothy | Dorothy Read Healing, East Stroudsburg"
        description="Dorothy Read brings over 30 years of experience to her individualized practice of bodywork and gentle energy work in East Stroudsburg, Pennsylvania."
        path="/about"
      />

      <section className="mx-auto max-w-6xl px-6 pb-16 pt-12 md:pt-20">
        <div className="grid items-start gap-10 md:grid-cols-12">
          <div className="md:col-span-5">
            <img
              src="/images/dorothy-portrait.jpg"
              alt="Dorothy Read seated in soft natural light"
              className="aspect-[4/5] w-full rounded-2xl object-cover object-top shadow-[0_24px_60px_rgba(66,82,68,0.18)]"
              loading="eager"
            />
            <p className="mt-4 text-sm text-muted-foreground">
              Dorothy Read, at her practice in East Stroudsburg
            </p>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <p className="eyebrow">About Dorothy</p>
            <h1 className="mt-4 font-serif text-5xl font-semibold leading-[1.05] tracking-tight text-primary">
              Thirty years of paying attention
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-foreground/90">
              Dorothy Read has practised bodywork and gentle energy work for over 30 years.
              What that time has taught her is simple: no two people arrive needing the same thing, and
              a session only works when it begins with listening.
            </p>
            <p className="mt-4 text-lg leading-relaxed text-foreground/90">
              Her manner is calm and unhurried. She explains what she suggests and why, checks in as she
              works, and shapes each visit around the person in the room - never around a fixed routine.
            </p>
            <p className="mt-4 text-lg leading-relaxed text-foreground/90">
              She works from {b.address_lines[0]} in East Stroudsburg, welcoming clients from Stroudsburg
              and the surrounding Pocono area.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-secondary/50">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-6 py-16 md:grid-cols-2 md:py-24">
          <div>
            <h2 className="font-serif text-4xl font-semibold tracking-tight text-primary">Her approach</h2>
            <ul className="mt-8 space-y-6 text-lg leading-relaxed">
              <li className="flex gap-4">
                <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-accent" aria-hidden />
                <span><strong className="font-semibold text-primary">Individual first.</strong> Every session starts with a conversation and is built around your answers - your preferences, your comfort, your day.</span>
              </li>
              <li className="flex gap-4">
                <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-accent" aria-hidden />
                <span><strong className="font-semibold text-primary">Plain speaking.</strong> No jargon and no promises she cannot keep. Dorothy tells you honestly what a session can and cannot offer.</span>
              </li>
              <li className="flex gap-4">
                <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-accent" aria-hidden />
                <span><strong className="font-semibold text-primary">Open to everyone.</strong> Energy work is available for those who are drawn to it, and never expected of those who are not. You are welcome exactly as you are.</span>
              </li>
            </ul>
          </div>
          <img
            src="/images/hero-garden.jpg"
            alt="Dorothy tending rosemary in a quiet, sunlit garden"
            className="aspect-[4/3] w-full rounded-2xl object-cover shadow-[0_16px_44px_rgba(66,82,68,0.14)]"
            loading="lazy"
          />
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-16 md:py-24" data-testid="about-studies">
        <div className="grid gap-10 md:grid-cols-2">
          <div>
            <p className="eyebrow">A lifetime of study</p>
            <h2 className="mt-4 font-serif text-4xl font-semibold tracking-tight text-primary">
              Experience and education
            </h2>
          </div>
          <div>
            <p className="text-lg leading-relaxed text-foreground/90">
              Dorothy Read brings over 30 years of experience and education in the world of therapy and
              energy work. Over her career there has been a consistent focus on providing services that
              are best for each client. In all cases Dorothy uses her deep knowledge of various therapies
              and holistic practices to ease muscular pain, reduce stress and support renewal - physical
              and mental.
            </p>
            <p className="mt-4 text-lg leading-relaxed text-foreground/90">
              Dorothy has studied at storied institutions and under renowned practitioners and
              educators, including:
            </p>
            <ul className="mt-6 grid gap-x-8 gap-y-3 sm:grid-cols-2">
              {STUDIES.map((s) => (
                <li key={s} className="flex items-baseline gap-3 text-lg" data-testid={`study-${s.split(",")[0].toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <span className="h-2 w-2 shrink-0 translate-y-[-2px] rounded-full bg-accent" aria-hidden />
                  <span className="font-medium text-primary">{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-16 text-center md:py-24" data-testid="about-cta">
        <h2 className="font-serif text-4xl font-semibold tracking-tight text-primary md:text-5xl">
          Come and talk with her
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-lg text-foreground/90">
          Sessions are 90 minutes and $125 - and two or three-visit packages bring the
          per-visit cost down. Schedule an appointment to begin.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <BookingCta location="about" testid="about-request-appointment" />
        </div>
      </section>
    </>
  );
}
